import { z } from 'zod';
import { Id, IsoDate } from './common';
import { SCORE_AXES, ScoreAxis } from './enums';
import { ConfidenceBand } from './opportunities';
import { OpportunityStatus, Verdict } from './transitions';

/**
 * Calibration (`scoring.md` § 8, `api.md` § Tableau de bord et calibration) : chaque opportunité
 * ayant une expérience terminée ou un résultat produit, avec le score **initial** (historique
 * `opportunity_events`, jamais le score rescoré) mis en regard du verdict et des résultats réels.
 * Aucun ajustement automatique : la première recalibration humaine est prévue après
 * `CALIBRATION_RECALIBRATION_AFTER` expériences terminées.
 */

/** Seuils de tranche de score (`scoring.md` § 4, mêmes paliers que `ScoreBadge`). */
export const CALIBRATION_SCORE_THRESHOLDS = [40, 55, 70] as const;
export const SCORE_BUCKETS = ['0-40', '40-55', '55-70', '70+'] as const;
export const ScoreBucket = z.enum(SCORE_BUCKETS);
export type ScoreBucket = z.infer<typeof ScoreBucket>;

/** Bandes de confiance (`scoring.md` § 5) : faible < 0,35, moyenne 0,35–0,65, élevée > 0,65. */
export const CALIBRATION_CONFIDENCE_THRESHOLDS = [0.35, 0.65] as const;

/** Nombre d'expériences terminées avant la première recalibration humaine. */
export const CALIBRATION_RECALIBRATION_AFTER = 10;

/** Tranche de score d'une valeur 0–100 (`null` → `null`). */
export function scoreBucketOf(score: number | null): ScoreBucket | null {
  if (score === null) return null;
  const [two, three, four] = CALIBRATION_SCORE_THRESHOLDS;
  if (score >= four) return '70+';
  if (score >= three) return '55-70';
  if (score >= two) return '40-55';
  return '0-40';
}

/** Bande de confiance d'une valeur 0–1 (`null` → `null`). */
export function confidenceBandOf(confidence: number | null): ConfidenceBand | null {
  if (confidence === null) return null;
  const [low, high] = CALIBRATION_CONFIDENCE_THRESHOLDS;
  if (confidence < low) return 'low';
  return confidence > high ? 'high' : 'medium';
}

/** Résultats produit d'une opportunité lancée (`product_outcomes`). */
export const CalibrationOutcome = z.object({
  launchedAt: IsoDate.nullable(),
  buildDays: z.number().nullable(),
  /** Dernière valeur de `product_outcomes.revenue` (métriques ordonnées par période). */
  revenueLatest: z.number().nullable(),
});
export type CalibrationOutcome = z.infer<typeof CalibrationOutcome>;

export const CalibrationRow = z.object({
  opportunityId: Id,
  title: z.string(),
  marketId: z.string(),
  status: OpportunityStatus,
  /** Score au moment de la création (événement `created`, sinon premier `rescored`). */
  initialScore: z.number().nullable(),
  currentScore: z.number().nullable(),
  reviewedScore: z.number().nullable(),
  /** Confiance initiale (même source que `initialScore`). */
  initialConfidence: z.number().nullable(),
  confidence: z.number().nullable(),
  /** Axes A–G du score système courant (0–1). */
  axes: z.record(ScoreAxis, z.number()).nullable(),
  verdict: Verdict.nullable(),
  verdictAt: IsoDate.nullable(),
  cpaUsd: z.number().nullable(),
  ctr: z.number().nullable(),
  strongSignals: z.number().int().nullable(),
  budgetSpentUsd: z.number().nullable(),
  outcome: CalibrationOutcome.nullable(),
});
export type CalibrationRow = z.infer<typeof CalibrationRow>;

const VerdictCounts = {
  validated: z.number().int(),
  invalidated: z.number().int(),
  inconclusive: z.number().int(),
};

export const CalibrationScoreBucket = z.object({ bucket: ScoreBucket, ...VerdictCounts });
export type CalibrationScoreBucket = z.infer<typeof CalibrationScoreBucket>;

export const CalibrationConfidenceBand = z.object({ band: ConfidenceBand, ...VerdictCounts });
export type CalibrationConfidenceBand = z.infer<typeof CalibrationConfidenceBand>;

/** Moyenne d'un axe pour les `validated` et pour les `invalidated` (0–1, `null` sans donnée). */
export const CalibrationAxis = z.object({
  axis: ScoreAxis,
  validated: z.number().nullable(),
  invalidated: z.number().nullable(),
});
export type CalibrationAxis = z.infer<typeof CalibrationAxis>;

export const CalibrationTotals = z.object({
  /** Expériences terminées représentées dans les lignes. */
  experiments: z.number().int(),
  ...VerdictCounts,
});
export type CalibrationTotals = z.infer<typeof CalibrationTotals>;

export const CalibrationResponse = z.object({
  rows: z.array(CalibrationRow),
  byScoreBucket: z.array(CalibrationScoreBucket).length(SCORE_BUCKETS.length),
  byConfidenceBand: z.array(CalibrationConfidenceBand).length(ConfidenceBand.options.length),
  axesByVerdict: z.array(CalibrationAxis).length(SCORE_AXES.length),
  totals: CalibrationTotals,
});
export type CalibrationResponse = z.infer<typeof CalibrationResponse>;

/** En-tête exact de `GET /calibration/analyses.csv` (`api.md`, fiche T29). */
export const CALIBRATION_CSV_HEADERS = [
  'opportunity_id',
  'title',
  'market',
  'analysis_id',
  'tier',
  'model',
  'prompt_version',
  'created_at',
  'confidence',
  'verdict',
  'human_annotation',
  'human_annotation_at',
] as const;

/** Préfixe conventionnel des commentaires d'audit d'analyse (`risks.md` § Audit des évaluations LLM). */
export const CALIBRATION_ANNOTATION_PREFIX = 'analyse:';
